import { ReactNode, useEffect, useState } from "react";

import { SignatureMethod } from "@smart/bridge-types-basic";
import { loadLocale } from "@smart/itops-locale-dom";
import {
  Badge,
  Checkbox,
  Icon,
  Loading,
  Modal,
  RadioButtons,
} from "@smart/itops-sb-design-system-dom";
import { useProviderInfo } from "@smart/itops-smokeball-components-dom";
import { useLoadInfoTrackSetupState } from "@smart/manage-gql-client-dom";

import { ChargeableBadge } from "./chargeable-badge";

type SignatureTypeModalProps = {
  open: boolean;
  initialMethod?: SignatureMethod;
  digitalDisabled?: boolean;
  standardSignatureDisabled?: boolean;
  onClose: () => void;
  onConfirm: (
    method: SignatureMethod,
    setAsDefault: boolean,
  ) => void | Promise<void>;
};

const FEATURES: Record<SignatureMethod, string[]> = {
  electronic: [
    "Draw or type signature",
    "Timestamp captured with submission",
    "Stored with the form record",
  ],
  digital: [
    "Cryptographically verifiable",
    "Full audit trail — timestamp, IP, device",
    "Stronger evidence if the signature is ever challenged",
  ],
};

const FeatureList = ({ features }: { features: string[] }) => (
  <ul className="flex flex-col gap-xSmall m-0 pl-0 list-none">
    {features.map((feature) => (
      <li key={feature} className="flex items-start gap-small text-neutral-800">
        <Icon
          name="solidCheck"
          className="w-[1.2rem] h-[1.2rem] mt-[0.2rem] text-blue-560 shrink-0"
        />
        {feature}
      </li>
    ))}
  </ul>
);

const SignatureOptionLabel = ({
  title,
  badges,
  description,
  features,
  disabledReason,
  loading,
}: {
  title: string;
  badges: ReactNode;
  description: string;
  features: string[];
  disabledReason?: string;
  loading?: boolean;
}) => (
  <div className="relative flex flex-col gap-small w-full min-w-0 flex-1 ml-[0.8rem] mt-[0.2rem]">
    <div className="flex items-center justify-between gap-small w-full">
      <span className="font-semibold text-ui text-neutral-850 shrink-0">
        {title}
      </span>
      <div className="flex items-center gap-small flex-wrap">{badges}</div>
    </div>
    <p className="text-ui text-neutral-500">{description}</p>
    <FeatureList features={features} />
    {disabledReason && (
      <p className="text-ui text-neutral-500 italic">{disabledReason}</p>
    )}
    {loading && <Loading size="small" />}
  </div>
);

export const SignatureTypeModal = ({
  open,
  initialMethod = "electronic",
  digitalDisabled,
  standardSignatureDisabled,
  onClose,
  onConfirm,
}: SignatureTypeModalProps) => {
  const { terms } = loadLocale();
  const providerInfo = useProviderInfo();
  const signatureProviderName =
    providerInfo?.label === "triConvey" ? "triSign" : "SignIT";
  const [selected, setSelected] = useState<SignatureMethod>(initialMethod);
  const [setAsDefault, setSetAsDefault] = useState(false);

  const { result: infoTrackSetup, loading: checkingSetup } =
    useLoadInfoTrackSetupState(
      {},
      {
        skip: !open,
        fetchPolicy: "cache-first",
      },
    );

  const infoTrackUnauthorized =
    !checkingSetup && infoTrackSetup?.authorized !== true;
  const isDigitalDisabled = !!digitalDisabled || infoTrackUnauthorized;

  const digitalDisabledReason = (() => {
    if (digitalDisabled) {
      return `Not available while this form has an active web ${terms.enquiry.name} link.`;
    }
    if (infoTrackUnauthorized) {
      return `Requires digital signature to be set up for your firm.`;
    }
    return undefined;
  })();

  useEffect(() => {
    if (open) {
      setSelected(
        standardSignatureDisabled && initialMethod === "electronic"
          ? "digital"
          : initialMethod,
      );
      setSetAsDefault(false);
    }
  }, [open, initialMethod, standardSignatureDisabled]);

  useEffect(() => {
    if (
      isDigitalDisabled &&
      selected === "digital" &&
      !standardSignatureDisabled
    ) {
      setSelected("electronic");
    }
  }, [isDigitalDisabled, selected, standardSignatureDisabled]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="default"
      dataTestId="choose-signature-type-modal"
      header={{ text: "Choose signature type" }}
      footer={{
        start: (
          <Checkbox
            label="Set as default for new signatures"
            checked={setAsDefault}
            onChange={(checked) => setSetAsDefault(checked)}
            dataTestId="set-signature-default"
          />
        ),
        buttons: [
          {
            text: "Cancel",
            variant: "secondarySubtle",
            onClick: onClose,
          },
          {
            text: "Confirm Type",
            variant: "primaryDefault",
            onClick: () => onConfirm(selected, setAsDefault),
            disabled:
              checkingSetup || (standardSignatureDisabled && isDigitalDisabled),
          },
        ],
      }}
    >
      <div className="flex flex-col gap-medium">
        <p className="m-0 text-ui text-neutral-500">
          Pick the level of signing this form needs. You can change this later.
        </p>

        <RadioButtons
          name="signature-method"
          direction="column"
          box
          withLabelWrapper={false}
          value={selected}
          onChange={(value) => setSelected(value as SignatureMethod)}
          options={[
            ...(standardSignatureDisabled
              ? []
              : [
                  {
                    value: "electronic",
                    label: "Standard Signature",
                    labelOverride: (
                      <SignatureOptionLabel
                        title="Standard Signature"
                        badges={
                          <>
                            <Badge
                              label="Included with Smokeball"
                              theme="emphasis"
                            />
                            <Badge label="Free" theme="success" />
                          </>
                        }
                        description={`Handwritten or typed signature. Best for routine intake and client ${terms.acknowledgement.plural}.`}
                        features={FEATURES.electronic}
                      />
                    ),
                  },
                ]),
            {
              value: "digital",
              label: "Verified Signature",
              disabled: isDigitalDisabled || checkingSetup,
              labelOverride: (
                <SignatureOptionLabel
                  title="Verified Signature"
                  badges={<ChargeableBadge />}
                  description={`Verified signature via ${signatureProviderName}. Best for anything that may be challenged.`}
                  features={FEATURES.digital}
                  disabledReason={digitalDisabledReason}
                  loading={checkingSetup}
                />
              ),
            },
          ]}
        />

        <p className="m-0 text-ui text-neutral-500">
          Verified signatures are billed to your firm. Clients won&apos;t see
          the charge.
        </p>
      </div>
    </Modal>
  );
};
